import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  MapPin,
  Rocket,
  Ear,
  Sparkles,
  Heart,
  Compass,
  Brain,
  GraduationCap,
  Mic,
  CalendarDays,
  Users,
  Mountain,
  Trophy,
  Network,
  Instagram,
  Linkedin,
  ArrowRight,
  Mail,
  MessageCircle,
} from "lucide-react";
import { InstagramFeed } from "@/components/sections/InstagramFeed";
import { CareersForm } from "@/components/forms/CareersForm";
import { KENTRO } from "@/lib/constants";
import { TikTokIcon } from "@/components/icons/TikTokIcon";

import heroImg from "@/assets/equipe-time-completo.jpg.asset.json";
import kentroDayImg from "@/assets/equipe-kentro-day.jpg.asset.json";
import acaoSocialImg from "@/assets/equipe-acao-social.jpg.asset.json";
import confraImg from "@/assets/equipe-confraternizacao.jpg.asset.json";
import selfieImg from "@/assets/equipe-bastidores-selfie.jpg.asset.json";
import podcastImg from "@/assets/kentro-podcast.jpg.asset.json";
import orgulhoImg from "@/assets/orgulho-de-ser-kentro.jpg.asset.json";
import feiraImg from "@/assets/kentro-feira-evento.jpg.asset.json";
import networkingImg from "@/assets/kentro-networking.jpg.asset.json";
import vitorLucasImg from "@/assets/vitor-lucas-kentro.jpg.asset.json";
import feedCopaTimeImg from "@/assets/equipe-feed/feed-copa-kentro-time.png.asset.json";
import feedBerrysImg from "@/assets/equipe-feed/feed-berrys-encontro.png.asset.json";
import feedAlbumCopaImg from "@/assets/equipe-feed/feed-album-copa-kentro.webp.asset.json";

const SITE = "https://kentro.com.br";
const PAGE_URL = `${SITE}/equipe`;
const TITLE =
  "Equipe e Cultura Kentro — conheça o time e os fundadores Vitor Barbalho (CEO) e Lucas Rocha (CTO)";
const DESC =
  "Conheça as pessoas por trás da Kentro: um time brasileiro 100% remoto, os fundadores Vitor Barbalho (CEO) e Lucas Rocha (CTO), a cultura, os rituais e os bastidores de quem constrói a plataforma todos os dias.";
const OG_IMAGE = vitorLucasImg.url;
const OG_IMAGE_ALT =
  "Vitor Barbalho (CEO) e Lucas Rocha (CTO), fundadores da Kentro Sistemas";

export const Route = createFileRoute("/equipe")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: OG_IMAGE_ALT },
      { property: "og:image:width", content: "1280" },
      { property: "og:image:height", content: "1920" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Equipe & Cultura", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Kentro Sistemas Inteligentes",
          url: SITE,
          logo: `${SITE}/favicon.svg`,
          description: DESC,
          foundingLocation: { "@type": "Place", name: "Montes Claros, MG, Brasil" },
          address: { "@type": "PostalAddress", streetAddress: KENTRO.address },
          email: KENTRO.email,
          founder: [
            {
              "@type": "Person",
              "@id": `${PAGE_URL}#vitor-barbalho`,
              name: "Vitor Barbalho",
              jobTitle: "Founder & CEO",
              worksFor: { "@type": "Organization", name: "Kentro Sistemas Inteligentes" },
              description:
                "Engenheiro de Telecomunicações com mais de 17 anos em TI. Fundou a Kentro em 2021 para construir empresas autogeridas, conscientes e humanizadas. Apresentador do Kentro Podcast e rosto público da marca.",
              sameAs: ["https://www.linkedin.com/in/vitorbarbalho/"],
            },
            {
              "@type": "Person",
              "@id": `${PAGE_URL}#lucas-rocha`,
              name: "Lucas Rocha",
              jobTitle: "Founder & CTO",
              worksFor: { "@type": "Organization", name: "Kentro Sistemas Inteligentes" },
              description:
                "CTO da Kentro, responsável por toda a tecnologia. Foco em IA aplicada a vendas e atendimento omnichannel, guiando o produto pelos pilares Inovação · Eficiência · Escala.",
              sameAs: ["https://www.linkedin.com/in/lucas-rocha-8bb13722b/"],
            },
          ],
          sameAs: [
            KENTRO.links.linkedin,
            KENTRO.links.instagram,
            KENTRO.links.youtube,
            KENTRO.links.tiktok,
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": `${PAGE_URL}#vitor-barbalho`,
              name: "Vitor Barbalho",
              jobTitle: "Founder & CEO",
              description:
                "Engenheiro de Telecomunicações com mais de 17 anos em TI. Fundou a Kentro em 2021 para construir empresas autogeridas, conscientes e humanizadas. Apresentador do Kentro Podcast e rosto público da marca.",
              image: `${SITE}${vitorLucasImg.url}`,
              url: PAGE_URL,
              sameAs: ["https://www.linkedin.com/in/vitorbarbalho/"],
              worksFor: {
                "@type": "Organization",
                name: "Kentro Sistemas Inteligentes",
                url: SITE,
              },
            },
            {
              "@type": "Person",
              "@id": `${PAGE_URL}#lucas-rocha`,
              name: "Lucas Rocha",
              jobTitle: "Founder & CTO",
              description:
                "CTO da Kentro, responsável por toda a tecnologia. Foco em IA aplicada a vendas e atendimento omnichannel, guiando o produto pelos pilares Inovação · Eficiência · Escala.",
              image: `${SITE}${vitorLucasImg.url}`,
              url: PAGE_URL,
              sameAs: ["https://www.linkedin.com/in/lucas-rocha-8bb13722b/"],
              worksFor: {
                "@type": "Organization",
                name: "Kentro Sistemas Inteligentes",
                url: SITE,
              },
            },
          ],
        }),
      },
    ],
  }),
  component: EquipePage,
});

// ---------- Helpers ----------
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" as const },
};

function SectionTitle({
  eyebrow,
  title,
  intro,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
}) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
          {eyebrow}
        </span>
      )}
      <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">{title}</h2>
      {intro && <p className="mt-4 text-base text-muted-foreground md:text-lg">{intro}</p>}
    </div>
  );
}

// ---------- Data ----------
const howWeWork = [
  {
    icon: MapPin,
    title: "100% remoto",
    text: "Um time distribuído pelo Brasil, com raízes em Montes Claros (MG) e jeito mineiro de acolher.",
  },
  {
    icon: Rocket,
    title: "Entregas toda semana",
    text: "Pequenos avanços constantes, sem perder qualidade. Velocidade composta no lugar de grandes saltos.",
  },
  {
    icon: Ear,
    title: "Perto de quem opera",
    text: "Cada release nasce de uma dor real de cliente — não de um quadro branco na sala de produto.",
  },
  {
    icon: Sparkles,
    title: "IA com propósito",
    text: "A tecnologia multiplica o impacto das pessoas. Automatizamos o repetitivo para abrir espaço para o que importa.",
  },
];

const values = [
  {
    icon: Heart,
    title: "Obsessão pelo cliente",
    text: "O suporte é o coração da Kentro. Resolver é o nosso esporte favorito — e a gente joga todo dia.",
  },
  {
    icon: Compass,
    title: "Clareza acima de tudo",
    text: "Produto, dados e contratos precisam fazer sentido para todos. Sem jargão, sem letras miúdas.",
  },
  {
    icon: Brain,
    title: "Tecnologia com propósito",
    text: "Usamos IA para liberar as pessoas do repetitivo — não para substituí-las.",
  },
  {
    icon: GraduationCap,
    title: "Gente que ensina gente",
    text: "Cultura de aprendizado contínuo, dentro e fora da Kentro Academy.",
  },
];

const rituals = [
  {
    icon: CalendarDays,
    title: "Kentro Day",
    text: "Encontros que reúnem o time inteiro, celebram resultados e fortalecem laços fora da tela.",
    img: kentroDayImg.url,
    alt: "Time da Kentro reunido no Kentro Day, em festa temática",
  },
  {
    icon: Users,
    title: "Encontros do time",
    text: "Momentos de alinhamento, troca de conhecimento e construção conjunta — presenciais e remotos.",
    img: selfieImg.url,
    alt: "Equipe da Kentro reunida em sala de encontro, sorrindo para selfie",
  },
  {
    icon: Mic,
    title: "Kentro Podcast",
    text: "Conversas sobre atendimento, vendas e IA. Disponível no YouTube, com novos episódios toda semana.",
    img: podcastImg.url,
    alt: "Cena do Kentro Podcast com apresentadores ao microfone",
    href: KENTRO.links.youtube,
  },
  {
    icon: Mountain,
    title: "Orgulho mineiro",
    text: "Nascida em Montes Claros (MG), a Kentro carrega a identidade e o acolhimento do interior em cada conversa.",
    img: acaoSocialImg.url,
    alt: "Time da Kentro reunido em ação social, com presentes para doação",
  },
];

const presence = [
  {
    icon: Trophy,
    title: "Eventos e feiras do setor",
    text: "Levamos a Kentro para feiras, congressos e conferências de tecnologia em todo o Brasil.",
    img: feiraImg.url,
    alt: "Equipe da Kentro no Web Summit Rio com placa de expositor",
  },
  {
    icon: Network,
    title: "Networking e ecossistema",
    text: "Trocamos experiências com líderes do ecossistema de empreendedorismo e inovação brasileiro.",
    img: networkingImg.url,
    alt: "Time da Kentro fazendo networking em evento de inovação",
  },
];

const gallery = [
  { img: feedCopaTimeImg.url, alt: "Time da Kentro com camisas do Brasil na Copa Kentro" },
  { img: feedBerrysImg.url, alt: "Encontro do time da Kentro na Berry's" },
  { img: feedAlbumCopaImg.url, alt: "Álbum Copa Kentro com figurinhas do time" },
  { img: selfieImg.url, alt: "Selfie do time da Kentro em encontro presencial" },
  { img: confraImg.url, alt: "Confraternização do time da Kentro em ambiente externo" },
  { img: heroImg.url, alt: "Foto do time completo da Kentro reunido" },
  { img: kentroDayImg.url, alt: "Kentro Day — time reunido em festa temática" },
  { img: acaoSocialImg.url, alt: "Time da Kentro em ação social" },
];

// ---------- Page ----------
function EquipePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden pt-24 pb-20 md:pt-28 md:pb-28">
        <div className="absolute inset-0">
          <img
            src={feedCopaTimeImg.url}
            alt="Time da Kentro reunido com camisas do Brasil na Copa Kentro"
            className="h-full w-full object-cover object-center"
            width={1600}
            height={1200}
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/75 to-background" />
          <div className="circuit-pattern absolute inset-0 opacity-20" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.span
            {...fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow"
          >
            Equipe & Cultura
          </motion.span>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-5 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl"
          >
            As pessoas por trás da <span className="gradient-text">Kentro</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground"
          >
            Tecnologia se constrói com gente. Por trás de cada automação e cada cliente que cresce com a
            Kentro existe um time brasileiro apaixonado pelo que faz.
          </motion.p>
        </div>
      </section>

      {/* INSTAGRAM EM TEMPO REAL */}
      <InstagramFeed />

      {/* INSTITUCIONAL → /sobre */}
      <section className="bg-secondary/30 py-14">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.h2 {...fadeUp} className="text-2xl font-bold tracking-tight md:text-3xl">
            Procurando a <span className="gradient-text">história da Kentro</span>?
          </motion.h2>
          <motion.p {...fadeUp} className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            A trajetória completa — fundação em 2021, marcos ano a ano, números de operação e
            certificações — agora está na página institucional.
          </motion.p>
          <motion.div {...fadeUp} className="mt-6">
            <Link
              to="/sobre"
              className="inline-flex items-center gap-2 rounded-full border border-primary-glow/40 bg-primary-glow/10 px-6 py-3 text-sm font-semibold text-foreground transition hover:bg-primary-glow/20"
            >
              Ver a página Sobre a Kentro <ArrowRight className="size-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* COMO TRABALHAMOS */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Como trabalhamos"
            title="Um jeito Kentro de fazer as coisas"
            intro="Quatro princípios simples que guiam a rotina do time — e definem como a gente entrega valor."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {howWeWork.map((c, i) => (
              <motion.div
                key={c.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="glass-card rounded-2xl p-6"
              >
                <div className="gradient-primary inline-flex size-11 items-center justify-center rounded-xl text-white shadow-glow">
                  <c.icon className="size-5" />
                </div>
                <h3 className="mt-4 font-semibold">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAIXA CULTURAL */}
      <section className="relative overflow-hidden py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-3xl border border-white/10 shadow-glow"
          >
            <img
              src={orgulhoImg.url}
              alt="Orgulho de ser Kentro"
              className="h-auto w-full object-cover"
              width={1400}
              height={400}
              loading="lazy"
              decoding="async"
            />
          </motion.div>
        </div>
      </section>

      {/* VALORES */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="O que nos move (na prática)"
            title="Valores que cabem no dia a dia"
            intro="Cultura não é cartaz na parede. São os pequenos comportamentos que se repetem toda semana."
          />

          <div className="mt-12 grid items-center gap-10 lg:grid-cols-2">
            <motion.div {...fadeUp} className="order-2 lg:order-1">
              <ul className="space-y-5">
                {values.map((v, i) => (
                  <motion.li
                    key={v.title}
                    {...fadeUp}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    className="glass-card flex gap-4 rounded-2xl p-5"
                  >
                    <div className="gradient-primary inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-white shadow-glow">
                      <v.icon className="size-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{v.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{v.text}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div {...fadeUp} className="order-1 lg:order-2">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-glow">
                <img
                  src={acaoSocialImg.url}
                  alt="Time da Kentro em ação social"
                  className="h-full w-full object-cover"
                  width={1200}
                  height={900}
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent p-5">
                  <p className="text-sm font-medium">
                    Mais que time: gente que escolhe estar perto, dentro e fora do trabalho.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* RITUAIS */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Rituais e momentos Kentro"
            title="A cultura que vive fora da tela"
            intro="Encontros, conversas e celebrações que mantêm o time conectado — independentemente da distância."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {rituals.map((r, i) => {
              const Card = (
                <motion.div
                  {...fadeUp}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="group glass-card overflow-hidden rounded-2xl"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.img}
                      alt={r.alt}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      width={800}
                      height={600}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="gradient-primary absolute left-4 top-4 inline-flex size-10 items-center justify-center rounded-xl text-white shadow-glow">
                      <r.icon className="size-5" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold">{r.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{r.text}</p>
                  </div>
                </motion.div>
              );
              return r.href ? (
                <a
                  key={r.title}
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block transition hover:-translate-y-0.5"
                >
                  {Card}
                </a>
              ) : (
                <div key={r.title}>{Card}</div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRESENÇA NO MERCADO */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Presença no mercado"
            title="A Kentro pelo Brasil"
            intro="Estamos onde o mercado conversa: feiras, conferências, ecossistema de inovação e empreendedorismo."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {presence.map((p, i) => (
              <motion.div
                key={p.title}
                {...fadeUp}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="glass-card overflow-hidden rounded-2xl"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.alt}
                    className="h-full w-full object-cover"
                    width={1200}
                    height={750}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="gradient-primary absolute left-4 top-4 inline-flex size-10 items-center justify-center rounded-xl text-white shadow-glow">
                    <p.icon className="size-5" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNDADORES */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Fundadores"
            title="Quem está à frente"
            intro="Dois fundadores complementares — um liderando mercado e cultura, outro liderando produto e engenharia — comandando um time multidisciplinar, 100% remoto, próximo de quem usa o produto todos os dias."
          />

          <div className="mt-12 grid items-stretch gap-8 lg:grid-cols-5">
            <motion.div
              {...fadeUp}
              className="overflow-hidden rounded-3xl border border-white/10 shadow-glow lg:col-span-2"
            >
              <img
                src={vitorLucasImg.url}
                alt="Vitor Barbalho (CEO) e Lucas Rocha (CTO), fundadores da Kentro"
                className="h-full w-full object-cover"
                width={1280}
                height={1920}
                loading="lazy"
                decoding="async"
              />
            </motion.div>

            <motion.div {...fadeUp} className="grid gap-6 lg:col-span-3 sm:grid-cols-2">
              <div className="glass-card flex h-full flex-col rounded-3xl p-7">
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
                  Founder & CEO
                </span>
                <h3 className="mt-4 text-2xl font-bold">Vitor Barbalho</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Engenheiro de Telecom com +17 anos em TI. Fundou a Kentro em 2021 para construir
                  empresas autogerenciáveis, autoconscientes e humanizadas. Hoje lidera estratégia,
                  cultura e a frente pública da marca no Kentro Podcast.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href="https://www.linkedin.com/in/vitorbarbalho/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
                  >
                    <Linkedin className="size-4" /> LinkedIn
                  </a>
                  <a
                    href={KENTRO.links.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
                  >
                    <Mic className="size-4" /> Podcast
                  </a>
                </div>
              </div>

              <div className="glass-card flex h-full flex-col rounded-3xl p-7">
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-glow/30 bg-primary-glow/10 px-3 py-1 text-xs font-medium uppercase tracking-wider text-primary-glow">
                  Founder & CTO
                </span>
                <h3 className="mt-4 text-2xl font-bold">Lucas Rocha</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Especialista em IA aplicada a vendas e atendimento omnichannel. Responsável por
                  toda a tecnologia da Kentro — guia o produto pelos pilares de inovação, eficiência
                  e escala, transformando empresas em máquinas de crescimento.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <a
                    href="https://www.linkedin.com/in/lucas-rocha-8bb13722b/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
                  >
                    <Linkedin className="size-4" /> LinkedIn
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VIDA NA KENTRO */}
      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            eyebrow="Vida na Kentro"
            title="Bastidores que contam histórias"
            intro="Selecionamos alguns momentos recentes — confraternizações, encontros e celebrações."
          />

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {gallery.map((g, i) => (
              <motion.div
                key={g.alt}
                {...fadeUp}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="group overflow-hidden rounded-2xl border border-white/10"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={g.img}
                    alt={g.alt}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    width={800}
                    height={800}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-center">
            <a
              href={KENTRO.links.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-primary-glow/40 bg-primary-glow/10 px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-primary-glow/20"
            >
              <Instagram className="size-4" /> Siga no Instagram
              <ArrowRight className="size-4" />
            </a>
            <a
              href={KENTRO.links.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Siga a Kentro no TikTok"
              className="inline-flex items-center gap-2 rounded-full border border-primary-glow/40 bg-primary-glow/10 px-5 py-3 text-sm font-semibold text-foreground transition hover:bg-primary-glow/20"
            >
              <TikTokIcon className="size-4" /> Siga no TikTok
              <ArrowRight className="size-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA FINAL DUPLO */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            title="Quer fazer parte dessa história?"
            intro="Dois caminhos para se aproximar — escolha o que faz mais sentido para você agora."
          />

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <motion.div {...fadeUp} className="glass-card flex flex-col rounded-3xl p-8">
              <div className="gradient-primary inline-flex size-12 items-center justify-center rounded-xl text-white shadow-glow">
                <MessageCircle className="size-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold">É cliente em potencial?</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Veja a Kentro funcionando com a sua operação, ao vivo. Sem compromisso.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/contato"
                  className="gradient-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:opacity-95"
                >
                  Agende uma demonstração <ArrowRight className="size-4" />
                </Link>
                <a
                  href={KENTRO.whatsappComercialUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
                >
                  <MessageCircle className="size-4" /> WhatsApp {KENTRO.whatsappDisplay}
                </a>
              </div>
            </motion.div>

            <motion.div {...fadeUp} className="glass-card flex flex-col rounded-3xl p-8">
              <div className="gradient-primary inline-flex size-12 items-center justify-center rounded-xl text-white shadow-glow">
                <Users className="size-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold">Quer trabalhar com a gente?</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Time remoto, brasileiro, com cultura de aprendizado contínuo. Mande seu currículo e
                conte um pouco da sua história.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={`mailto:rh@kentro.com.br?subject=${encodeURIComponent("Quero fazer parte da Kentro")}`}
                  className="gradient-primary inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:opacity-95"
                >
                  <Mail className="size-4" /> Fale com a gente
                </a>
                <a
                  href={KENTRO.links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-muted-foreground transition hover:border-primary-glow/40 hover:text-foreground"
                >
                  <Linkedin className="size-4" /> LinkedIn da Kentro
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRABALHE CONOSCO */}
      <section id="trabalhe-conosco" className="scroll-mt-24 bg-secondary/30 py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionTitle
            title="Trabalhe conosco"
            intro="Envie seus dados e o link do seu currículo. Guardamos seu perfil para as próximas vagas abertas."
          />
          <motion.div {...fadeUp} className="glass-card mt-10 rounded-3xl p-6 md:p-8">
            <CareersForm />
          </motion.div>
        </div>
      </section>
    </>
  );
}
